import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import hi from './locales/hi.json';

/**
 * Phase 7aa — i18n bootstrap.
 *
 * react-i18next 15 + i18next core. Two locales seeded — English (the
 * baseline / fallback) and Hindi (तत्काल देवनागरी). The chosen locale is:
 *   1. user.locale (Inertia shared prop)  — picked first
 *   2. localStorage('locale')              — operator's last choice
 *   3. 'en'                                — fallback
 *
 * Translation files use nested key groups: `nav.dashboard`, `common.save`,
 * `settings.language.title`, etc. When a key isn't present in `hi.json` the
 * English string is used unchanged (so the UI never shows raw {{key}}).
 *
 * Hindi number formatting note: Indian numerals are kept as Western digits
 * (0-9) since Indian business runs in those — Devanagari numerals (०-९)
 * are uncommon outside literature.
 */
export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
] as const;

export type LocaleCode = typeof SUPPORTED_LOCALES[number]['code'];

const STORAGE_KEY = 'locale';

function detectInitial(): LocaleCode {
  // Try local storage first — operator's persisted choice
  const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored === 'en' || stored === 'hi') return stored;
  return 'en';
}

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      hi: { translation: hi },
    },
    lng: detectInitial(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // react already escapes
    },
    returnEmptyString: false,
    react: {
      useSuspense: false,
    },
  });

/** Persist the operator's chosen locale and switch live. */
export function setLocale(locale: LocaleCode): void {
  void i18n.changeLanguage(locale);
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, locale);
  }
}

/** Synchronise with user.locale from Inertia shared props (call once on bootstrap). */
export function syncFromUserLocale(userLocale?: string | null): void {
  if (!userLocale) return;
  if (userLocale === 'en' || userLocale === 'hi') {
    if (i18n.language !== userLocale) {
      void i18n.changeLanguage(userLocale);
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, userLocale);
      }
    }
  }
}

export default i18n;
